import { useNavigate } from "react-router-dom";
import { useModalStore } from "../../store/useModalStore";
import * as React from "react";
import type { getQuotationListForm, QUOTATION_CATEGORY_TYPE } from "../../lib/apis/dtos/request/quotation.request";
import { useMutation, useQuery } from "@tanstack/react-query";
import { patchStoreBan, patchStoreBanRevert } from "../../lib/apis/v1/store.service";
import { Popover } from "@mui/material";
import ConfirmPopover from "../../components/ui/popover/confirm.component";
import { usePopover } from "../../store/usePopoverStore";
import { getQueryClient } from "../../config/queryClient.config";
import SuccessSnackbar from "../../components/ui/snackbar/success.component";
import FailSnackbar from "../../components/ui/snackbar/fail.component";
import { cn } from "../../utils/cn.utils";
import { useAuth } from "../../store/useAuthStore";
import { ROLES } from "../../types/auth.types";
import { useQuotationList } from "../../lib/apis/hooks/quotation.hook";
import { useEnterKey } from "../../lib/hooks/useEnterKey";
import SearchInput from "../../components/ui/input/search.component";
import {
  ButtonGroup,
  Button as MuiButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import Button from "../../components/ui/button";
import { MODAL_KEY } from "../../lib/constants/modal.constants";

const CATEGORY_TYPE = [
  { label: "전체", value: "ALL" },
  { label: "제출 완료", value: "SUBMITTED" },
  { label: "제출 미완료", value: "UNSUBMITTED" },
];

const DEFAULT_PAGE_SIZE = 10;

interface BanCellProps {
  isActive: boolean;
  isBanned?: boolean;
  banTo?: string | null;
  onBan: () => void;
  onRevert: (event: React.MouseEvent<HTMLElement>) => void;
}

/** 지점 정지 컬럼 — 활성 지점만 정지 가능, 정지된 지점은 해제 노출 */
function BanCell({ isActive, isBanned, banTo, onBan, onRevert }: BanCellProps) {
  if (isBanned) {
    return (
      <div className="text-center whitespace-nowrap">
        <p className="text-black/80">정지됨 (~{banTo || "-"})</p>
        <span
          className="text-primary-50 cursor-pointer font-medium hover:underline"
          onClick={onRevert}
        >
          지점 정지 해제
        </span>
      </div>
    );
  }

  return (
    <span
      className={cn(
        "font-medium",
        isActive
          ? "text-red-50 cursor-pointer hover:underline"
          : "text-black/30 cursor-default",
      )}
      onClick={() => isActive && onBan()}
    >
      지점 정지 처리
    </span>
  );
}

export default function QuotationPage() {
  // ROUTER
  const navigate = useNavigate();
  const { openModal } = useModalStore();
  const auth = useAuth();
  const pushPopover = usePopover((state) => state.pushPopover);
  const popPopover = usePopover((state) => state.popPopover);
  // 지점 정지는 어드민 전용 (이 화면은 OWNER/PARTNER 의 '시세표 입력' 과 같은 경로를 씀)
  const isAdmin = auth.role === ROLES.ADMIN;

  const [form, setForm] = React.useState<getQuotationListForm>({
    category: "ALL",
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    search: "",
  });

  const { data, refetch, isSuccess } = useQuery({
    ...useQuotationList(form),
    enabled: true,
  });
  const { result } = data || {};
  const content = result?.content || [];
  const page = result?.page;

  // HANDLER
  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setForm((state) => ({ ...state, page: newPage }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((state) => ({
      ...state,
      page: 0,
      size: parseInt(event.target.value, 10),
    }));
  };

  useEnterKey(refetch);

  const banMutation = useMutation({
    mutationFn: patchStoreBan,
    onSuccess: (res) => {
      if (res.error) {
        FailSnackbar(res.message || "지점 정지 실패");
        return;
      }
      SuccessSnackbar("지점 정지 처리 완료");
      getQueryClient().invalidateQueries({ queryKey: ["quotation-list"] });
      getQueryClient().invalidateQueries({ queryKey: ["store-list"] });
      getQueryClient().invalidateQueries({ queryKey: ["store-detail"] });
      getQueryClient().invalidateQueries({ queryKey: ["store-date-log"] });
    },
    onError: () => FailSnackbar("지점 정지 실패"),
  });

  const openBanModal = (storeId: number) =>
    openModal(MODAL_KEY.BranchSuspend, {
      onConfirm: (day) => banMutation.mutate({ id: storeId, day }),
    });

  const banRevertMutation = useMutation({
    mutationFn: patchStoreBanRevert,
    onSuccess: (res) => {
      if (res.error) {
        FailSnackbar(res.message || "지점 정지 해제 실패");
        return;
      }
      SuccessSnackbar("지점 정지 해제 완료");
      getQueryClient().invalidateQueries({ queryKey: ["quotation-list"] });
      getQueryClient().invalidateQueries({ queryKey: ["store-list"] });
      getQueryClient().invalidateQueries({ queryKey: ["store-detail"] });
      getQueryClient().invalidateQueries({ queryKey: ["store-date-log"] });
    },
    onError: () => FailSnackbar("지점 정지 해제 실패"),
  });

  const openBanRevertPopover = (storeId: number, event: React.MouseEvent<HTMLElement>) =>
    pushPopover(
      Popover,
      event.currentTarget,
      {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        transformOrigin: { vertical: "top", horizontal: "center" },
        PaperProps: { sx: { mt: 1 } },
      },
      (popoverId) => (
        <ConfirmPopover
          id={popoverId}
          rightProps={{
            label: "지점 정지 해제",
            variant: "primary",
            onClick: () => {
              popPopover(popoverId);
              banRevertMutation.mutate(storeId);
            },
          }}
        >
          <p className="text-[14px]">
            지점 정지를 해제하시겠습니까? 해당 지점이 활성화되고 시세표를 작성할 수 있게됩니다.
          </p>
        </ConfirmPopover>
      ),
    );

  return (
    <div className="p-6">
      <div className="p-[16px]">
        <SearchInput
          onChange={(e) =>
            setForm((state) => ({ ...state, search: e.target.value }))
          }
          label="지점 검색"
          placeholder="지점명, 지점 담당자명으로 검색"
          onClick={() => refetch()}
        />
        <ButtonGroup
          className="mt-[24px] float-right"
          variant="contained"
          aria-label="phone-group"
        >
          {CATEGORY_TYPE.map(({ label, value }, index) => (
            <MuiButton
              key={index}
              style={{
                backgroundColor:
                  form.category === value
                    ? "rgba(25, 118, 210, 0.5)"
                    : undefined,
              }}
              variant={form.category == value ? "contained" : "outlined"}
              onClick={() =>
                setForm((state) => ({
                  ...state,
                  page: 0,
                  category: value as QUOTATION_CATEGORY_TYPE,
                }))
              }
            >
              {label}
            </MuiButton>
          ))}
        </ButtonGroup>
      </div>

      <TableContainer>
        <Table className="w-full">
          <TableHead>
            <TableRow>
              <TableCell>지점명</TableCell>
              <TableCell className="w-[288px]">영업 지역</TableCell>
              <TableCell className="w-[88px] whitespace-nowrap text-center">활성 여부</TableCell>
              <TableCell className="w-[120px] whitespace-nowrap text-center">제출 여부</TableCell>
              <TableCell className="w-[120px] whitespace-nowrap text-center">제출 가격 보기</TableCell>
              <TableCell className="w-[120px] whitespace-nowrap text-center">담당자명</TableCell>
              <TableCell className="w-[160px] whitespace-nowrap text-center">시세표 복사하기</TableCell>
              {isAdmin && (
                <TableCell className="w-[160px] whitespace-nowrap text-center">지점 정지</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isSuccess &&
              (content || []).map(
                (
                  {
                    storeId,
                    ownerId,
                    storeName,
                    status,
                    quotationId,
                    publisherName,
                    isActive: isActiveRaw,
                    mainZone,
                    subZones,
                    isBanned,
                    banTo,
                  },
                  index
                ) => {
                  const submitText =
                    status == "SUBMITTED" ? "제출 완료" : "제출 미완료";
                  // 정지된 지점은 비활성으로 표기
                  const isActive = isActiveRaw && !isBanned;
                  const statusMap = {
                    활성: "primary",
                    비활성: "gray",
                  } as const;
                  type statusKey = keyof typeof statusMap;
                  const statused: statusKey = isActive ? "활성" : "비활성";
                  const color = statusMap[statused];
                  return (
                    <TableRow key={`model_${index}`}>
                      <TableCell>
                        <p className="text-ellipsis line-clamp-1">{storeName || "-"}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex-row gap-2">
                          {mainZone?.region === "온라인" ? (
                            <p className="text-style-body-03 font-normal">온라인</p>
                          ) : (
                            <>
                              <div className="flex-row gap-1">
                                <p className="text-style-compact-02 font-semibold px-2 py-1 rounded-1 bg-green-05 text-green-60">주</p>
                                <p className="text-style-body-03 font-normal">{mainZone.region} {mainZone.zoneName}</p>
                              </div>
                              {subZones.length > 0 && (
                                <>
                                  <div className="flex-row gap-1">
                                    <p className="text-style-compact-02 font-semibold px-2 py-1 rounded-1 bg-gray-05 text-gray-60">인근</p>
                                    <p className="text-style-body-03 font-normal">{subZones[0].region} {subZones[0].zoneName} {subZones.length - 1 > 0 ? `외 ${subZones.length - 1}개` : ""}</p>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant={color}
                          label={isActive ? "활성" : "비활성"}
                          className="h-6 px-[10px] rounded-[100px] text-[13px] font-normal"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant={
                            submitText == "제출 완료" ? "green" : "danger"
                          }
                          label={submitText}
                          className="h-6 px-[10px] rounded-[100px] text-[13px] font-normal"
                        />
                      </TableCell>
                      <TableCell>
                        <span
                          onClick={() => navigate(`/quotation/${storeId}`)}
                          className={
                            "text-[#1976D2] hover:underline underline-offset-1 cursor-pointer font-medium"
                          }
                        >
                          {quotationId == null ? "추가하기" : "가격보기"}
                        </span>
                      </TableCell>
                      <TableCell>{publisherName || "-"}</TableCell>
                      <TableCell>
                        <Button
                          disabled={quotationId == null ? true : false}
                          variant={quotationId == null ? "disabled" : "outline"}
                          label="시세표 복사하기"
                          onClick={() => openModal(MODAL_KEY.QuotationPaste, { storeId: storeId.toString(), ownerId: ownerId?.toString() || "" })} />
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-center whitespace-nowrap">
                          <BanCell
                            isActive={isActive}
                            isBanned={isBanned}
                            banTo={banTo}
                            onBan={() => openBanModal(storeId)}
                            onRevert={(event) => openBanRevertPopover(storeId, event)}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                }
              )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={page?.totalElements || 0}
        rowsPerPage={form.size}
        page={form.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
}
